import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";
import { FaFileAlt, FaRobot, FaMicrophone, FaQuestionCircle, FaChartBar } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { downloadConsultantCv } from "../../../api/consultantsApi";
import {
  listCvDocuments,
  downloadDocumentGroupDeliverable,
} from "../../../api/documentsApi";
import { EmptyState, MiniIconButton } from "../../../components/Portal/ui";

const DELIVERABLE_ICONS = {
  regular_cv_url: { icon: FaFileAlt, kind: "regular", labelKey: "asapCV.card.deliverables.originalCv" },
  ai_cv_url: { icon: FaRobot, kind: "ai", labelKey: "asapCV.card.deliverables.aiCv" },
  oral_guideline_url: { icon: FaMicrophone, kind: "oral", labelKey: "asapCV.card.deliverables.oralGuidelines" },
  qa_url: { icon: FaQuestionCircle, kind: "qa", labelKey: "asapCV.card.deliverables.qaDocument" },
  ao_scoring_url: { icon: FaChartBar, kind: "ao_scoring", labelKey: "asapCV.card.deliverables.aoScoring" },
};

// Tiny uppercase section label (template `.label`).
const SectionLabel = ({ children, ...rest }) => (
  <Text
    fontSize="10px"
    textTransform="uppercase"
    fontWeight="800"
    letterSpacing="0.055em"
    color="portal.muted"
    sx={{ marginBottom: "0 !important" }}
    {...rest}
  >
    {children}
  </Text>
);

const ConsultantSkillsPortfolioTab = ({
  consultantId,
  consultantName,
  hasCv,
}) => {
  const { t } = useTranslation("portal");
  const navigate = useNavigate();
  const { selectedBusinessUnitId, features } = useBusinessUnit();
  const hasCvModule = features?.has_cv;

  const [cvGroups, setCvGroups] = useState([]);
  const [loadingCvDocs, setLoadingCvDocs] = useState(false);
  const [downloadingStoredCv, setDownloadingStoredCv] = useState(false);

  const fetchCvDocuments = useCallback(async () => {
    if (!consultantId) return;
    setLoadingCvDocs(true);
    try {
      const payload = await listCvDocuments(consultantId, {
        buId: selectedBusinessUnitId || undefined,
      });
      let groups = [];
      if (Array.isArray(payload)) {
        groups = payload;
      } else if (payload && Array.isArray(payload.items)) {
        groups = payload.items;
      } else if (payload && Array.isArray(payload.data)) {
        groups = payload.data;
      }
      setCvGroups(groups || []);
    } catch (e) {
      console.error("Failed to fetch CV documents for consultant", e);
    } finally {
      setLoadingCvDocs(false);
    }
  }, [consultantId, selectedBusinessUnitId]);

  useEffect(() => {
    fetchCvDocuments();
  }, [fetchCvDocuments]);

  const handleDownloadStoredCv = async () => {
    if (!consultantId) return;
    setDownloadingStoredCv(true);
    try {
      const response = await downloadConsultantCv(consultantId, {
        buId: selectedBusinessUnitId || undefined,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const dispo = response.headers["content-disposition"] || "";
      const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(dispo);
      const filename = match ? match[1].replace(/["']/g, "") : `${consultantName || "consultant"}_cv.pdf`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to download stored CV", e);
    } finally {
      setDownloadingStoredCv(false);
    }
  };

  const handleDownloadDeliverable = async (reqId, kind, filenameBase) => {
    try {
      const response = await downloadDocumentGroupDeliverable(reqId, kind, {
        buId: selectedBusinessUnitId || undefined,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition = response.headers["content-disposition"];
      let filename = `${filenameBase}_${kind}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to download document", e);
    }
  };

  const handleSendCv = () => {
    const params = new URLSearchParams();
    params.set("consultantId", consultantId);
    if (consultantName) params.set("consultantName", consultantName);
    if (hasCv) params.set("loadCv", "1");
    navigate(`/portal/asap-cv?${params.toString()}`);
  };

  return (
    <VStack align="stretch" spacing="14px">
      {/* Source CV — the raw uploaded file on the consultant */}
      <Box>
        <Flex justify="space-between" align="center" mb="10px" wrap="wrap" gap="8px">
          <Heading
            as="h3"
            fontSize="14px"
            letterSpacing="-0.02em"
            color="portal.text"
            sx={{ marginBottom: "0 !important" }}
          >
            {t("pilotConsultants.detail.skillsPortfolio.sourceCvs", {
              defaultValue: "Source CV",
            })}
          </Heading>
          <Button
            size="sm"
            variant="primary"
            onClick={handleSendCv}
            isDisabled={!hasCvModule}
          >
            {t("pilotConsultants.detail.skillsPortfolio.sendCv", {
              defaultValue: "Send a CV",
            })}
          </Button>
        </Flex>

        {hasCv ? (
          <Flex
            justify="space-between"
            align="center"
            gap="8px"
            borderWidth="1px"
            borderColor="portal.line"
            borderRadius="10px"
            bg="portal.soft"
            px="12px"
            py="10px"
          >
            <Text
              fontSize="13px"
              fontWeight="600"
              color="portal.text"
              isTruncated
              sx={{ marginBottom: "0 !important" }}
            >
              {consultantName ||
                t("pilotConsultants.detail.skillsPortfolio.storedCv", {
                  defaultValue: "Stored CV",
                })}
            </Text>
            <Tooltip
              label={t("pilotConsultants.detail.skillsPortfolio.downloadSourceCv", {
                defaultValue: "Download source CV",
              })}
              hasArrow
            >
              <MiniIconButton
                aria-label={t("pilotConsultants.detail.skillsPortfolio.downloadSourceCv", {
                  defaultValue: "Download source CV",
                })}
                icon={<DownloadIcon />}
                onClick={handleDownloadStoredCv}
                isLoading={downloadingStoredCv}
              />
            </Tooltip>
          </Flex>
        ) : (
          <Text fontSize="13px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
            {t("pilotConsultants.detail.skillsPortfolio.noSourceCvs", {
              defaultValue: "No source CV uploaded yet.",
            })}
          </Text>
        )}
      </Box>

      <Divider borderColor="portal.line" />

      {/* Generated CVs — DocumentGroup cards with all deliverables */}
      <Box>
        <Heading
          as="h3"
          fontSize="14px"
          letterSpacing="-0.02em"
          color="portal.text"
          mb="10px"
          sx={{ marginBottom: "10px !important" }}
        >
          {t("pilotConsultants.detail.skillsPortfolio.generatedCvs", {
            defaultValue: "Generated CVs",
          })}
        </Heading>

        {!hasCvModule ? (
          <EmptyState
            icon={<FaFileAlt />}
            title={t("pilotConsultants.detail.skillsPortfolio.moduleNotEnabled", {
              defaultValue: "CV module not enabled for this business unit.",
            })}
          />
        ) : loadingCvDocs ? (
          <Flex align="center" justify="center" py="32px">
            <Spinner size="sm" color="portal.primary" />
          </Flex>
        ) : cvGroups.length === 0 ? (
          <EmptyState
            icon={<FaFileAlt />}
            title={t("pilotConsultants.detail.skillsPortfolio.noGeneratedCvs", {
              defaultValue: "No processed CVs yet.",
            })}
          />
        ) : (
          <VStack align="stretch" spacing="8px" maxH="400px" overflowY="auto">
            {cvGroups.map((g) => {
              const fullName =
                `${g.candidate_first_name || ""} ${g.candidate_last_name || ""}`.trim() ||
                consultantName ||
                g.req_id;
              const score =
                g.assessment_score != null ? g.assessment_score : null;
              const updatedDate = g.updated_at
                ? new Date(g.updated_at).toLocaleString()
                : g.created_at
                ? new Date(g.created_at).toLocaleString()
                : "";

              const availableDeliverables = Object.entries(DELIVERABLE_ICONS).filter(
                ([key]) => Boolean(g[key])
              );

              return (
                <Box
                  key={g.id || g.req_id}
                  borderWidth="1px"
                  borderColor="portal.line"
                  borderRadius="10px"
                  bg="portal.panel"
                  boxShadow="kpi"
                  p="12px"
                >
                  <HStack justify="space-between" mb="4px" gap="8px">
                    <Text
                      fontSize="13px"
                      fontWeight="700"
                      color="portal.text"
                      isTruncated
                      sx={{ marginBottom: "0 !important" }}
                    >
                      {fullName}
                    </Text>
                    {score != null && (
                      <Badge colorScheme="purple" flexShrink={0}>
                        {t("asapSkills.card.scoreTag", { score })}
                      </Badge>
                    )}
                  </HStack>
                  <Box mb={availableDeliverables.length > 0 ? "10px" : "0"}>
                    <SectionLabel>
                      {t("asapCV.card.updatedAt", { date: updatedDate })}
                    </SectionLabel>
                  </Box>
                  {availableDeliverables.length > 0 && (
                    <HStack spacing="6px">
                      {availableDeliverables.map(([key, { icon: IconComp, kind, labelKey }]) => (
                        <Tooltip key={key} label={t(labelKey)} hasArrow>
                          <MiniIconButton
                            aria-label={t(labelKey)}
                            icon={<IconComp />}
                            onClick={() => handleDownloadDeliverable(g.req_id, kind, fullName)}
                          />
                        </Tooltip>
                      ))}
                    </HStack>
                  )}
                </Box>
              );
            })}
          </VStack>
        )}
      </Box>
    </VStack>
  );
};

export default ConsultantSkillsPortfolioTab;
